import { useState, useEffect } from "react";
import axios from "axios";
import { ChevronLeft, Truck, Package, Clock, RotateCcw, MapPin, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShippingPage({ onBack }) {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    axios.get(`${API}/shipping-info`)
      .then((r) => setInfo(r.data))
      .catch(() => {});
  }, []);

  if (!info) return null;

  return (
    <div data-testid="shipping-page" className="max-w-4xl mx-auto pt-24 pb-16 px-4">
      <button
        data-testid="shipping-back-btn"
        onClick={onBack}
        className="flex items-center gap-1.5 text-[#666] hover:text-white text-sm mb-6 transition-colors"
      >
        <ChevronLeft size={16} /> Terug naar shop
      </button>

      <div className="mb-10">
        <h1 className="text-white text-3xl sm:text-4xl font-bold font-['Orbitron'] mb-2">
          Verzendkosten <span className="text-[#FF3B30]">&</span> Levertijd
        </h1>
        <p className="text-[#666] text-sm">Alles wat je moet weten over verzending en levering</p>
      </div>

      {/* Shipping Rates Table */}
      <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl overflow-hidden mb-8">
        <div className="p-5 border-b border-[#1E1E1E] flex items-center gap-3">
          <Truck size={20} className="text-[#FF3B30]" />
          <h2 className="text-white font-bold text-lg">Verzendtarieven</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#1E1E1E]">
                <th className="text-left px-5 py-3 text-[#666] text-xs uppercase tracking-wider font-semibold">Land</th>
                <th className="text-left px-5 py-3 text-[#666] text-xs uppercase tracking-wider font-semibold">Kosten</th>
                <th className="text-left px-5 py-3 text-[#666] text-xs uppercase tracking-wider font-semibold">Gratis vanaf</th>
                <th className="text-left px-5 py-3 text-[#666] text-xs uppercase tracking-wider font-semibold">Levertijd</th>
                <th className="text-left px-5 py-3 text-[#666] text-xs uppercase tracking-wider font-semibold">Vervoerder</th>
              </tr>
            </thead>
            <tbody>
              {info.shipping_rates.map((rate) => (
                <tr key={rate.country} className="border-b border-[#1E1E1E]/50 hover:bg-[#1A1A1A] transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <MapPin size={14} className="text-[#FF3B30]" />
                      <span className="text-white font-medium text-sm">{rate.country}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-[#FF3B30] font-bold text-sm">
                    {new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(rate.cost)}
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-[#27AE60] font-semibold text-sm">
                      {new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(rate.free_above)}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1.5">
                      <Clock size={12} className="text-[#555]" />
                      <span className="text-[#ccc] text-sm">{rate.delivery_days}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-[#999] text-sm">{rate.carrier}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Important Notes */}
      <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl overflow-hidden mb-8">
        <div className="p-5 border-b border-[#1E1E1E] flex items-center gap-3">
          <Package size={20} className="text-[#FF3B30]" />
          <h2 className="text-white font-bold text-lg">Belangrijke informatie</h2>
        </div>
        <div className="p-5 space-y-4">
          {info.notes.map((note) => (
            <div key={note} className="flex gap-3 items-start">
              <CheckCircle size={16} className="text-[#FF3B30] mt-0.5 flex-shrink-0" />
              <p className="text-[#999] text-sm leading-relaxed">{note}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Returns Policy */}
      <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl overflow-hidden">
        <div className="p-5 border-b border-[#1E1E1E] flex items-center gap-3">
          <RotateCcw size={20} className="text-[#FF3B30]" />
          <h2 className="text-white font-bold text-lg">Retourbeleid</h2>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-[#FF3B30]/10 text-[#FF3B30] font-bold text-sm px-3 py-1.5 rounded-lg border border-[#FF3B30]/30">
              {info.returns.period} bedenktijd
            </span>
          </div>
          <p className="text-[#999] text-sm leading-relaxed">{info.returns.description}</p>
        </div>
      </div>
    </div>
  );
}
